/**
 * Custom hook for role-based permission checking
 * Provides easy access to user permissions and role checking functions
 */

import { useAuth } from './useAuth';
import { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions, 
    getUserPermissions, 
    isAdmin, 
    hasRole,
    Permission,
    UserRole 
} from '../utils/rolePermissions';

export const usePermissions = () => {
    const { user } = useAuth();
    const userGroups = user?.userGroups;

    return {
        // Core permission checking functions
        hasPermission: (permission: Permission) => hasPermission(userGroups, permission),
        hasAnyPermission: (permissions: Permission[]) => hasAnyPermission(userGroups, permissions),
        hasAllPermissions: (permissions: Permission[]) => hasAllPermissions(userGroups, permissions),
        
        // Role checking functions
        hasRole: (role: UserRole) => hasRole(userGroups, role),
        isAdmin: () => isAdmin(userGroups),
        
        // Utility functions
        getUserPermissions: () => getUserPermissions(userGroups),
        userGroups: userGroups || [],
        
        // Common permission checks for UI components
        canViewDashboard: () => hasPermission(userGroups, Permission.VIEW_DASHBOARD),
        canViewLoanBook: () => hasPermission(userGroups, Permission.VIEW_LOAN_BOOK),
        canViewAccounts: () => hasPermission(userGroups, Permission.VIEW_ACCOUNTS),
        canViewApplications: () => hasPermission(userGroups, Permission.VIEW_APPLICATIONS),
        canViewCollections: () => hasPermission(userGroups, Permission.VIEW_COLLECTIONS),
        canViewReports: () => hasPermission(userGroups, Permission.VIEW_REPORTS),
        canViewCustomers: () => hasPermission(userGroups, Permission.VIEW_CUSTOMERS),
        canViewDocuments: () => hasPermission(userGroups, Permission.VIEW_DOCUMENTS),
        canManageUsers: () => hasPermission(userGroups, Permission.MANAGE_USERS),
        canAccessSettings: () => hasPermission(userGroups, Permission.SYSTEM_SETTINGS),
        canViewNotifications: () => hasPermission(userGroups, Permission.VIEW_NOTIFICATIONS),
        
        // Loan processing permissions
        canPerformVerification: () => hasPermission(userGroups, Permission.PERFORM_VERIFICATION),
        canPerformCreditAssessment: () => hasPermission(userGroups, Permission.PERFORM_CREDIT_ASSESSMENT),
        canManageLoanAgreement: () => hasPermission(userGroups, Permission.MANAGE_LOAN_AGREEMENT),
        canVerifyCollateral: () => hasPermission(userGroups, Permission.VERIFY_COLLATERAL),
        canManageDisbursements: () => hasPermission(userGroups, Permission.MANAGE_DISBURSEMENTS),
        
        // Check if user can access loan processing history
        canAccessLoanProcessingHistory: () => hasAnyPermission(userGroups, [
            Permission.PERFORM_VERIFICATION,
            Permission.PERFORM_CREDIT_ASSESSMENT,
            Permission.MANAGE_LOAN_AGREEMENT,
            Permission.VERIFY_COLLATERAL,
            Permission.MANAGE_DISBURSEMENTS
        ])
    };
};
